import { normalize, denormalize } from "normalizr";
import { of, timer, merge } from "rxjs";
import { filter, bufferTime } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { ServerResponseStatus } from "../../api/types.js";
import "immer";
import "moment-timezone";
import { equals } from "../../utils/object.js";
import "react-intl";
import "@apollo/client/errors";
import "@apollo/client";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import { switchMap, map, mergeMap } from "../../otel/rxjs.js";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import { BaseEpicsBuilder } from "../epicsBuilder.js";
import "../actions.js";
import "fast-deep-equal/es6";
import "redux";
import { getEntityActions } from "../entity/actions.js";
import "../entity/epics.js";
import { entitiesSelector } from "../entity/selectors.js";
import "../entity/EntityProvider.js";
import { getViewActions } from "../view/actions.js";
import { getRealtimeActions } from "./actions.js";
import { realtimeSelector } from "./selectors.js";
import { RealtimeUpdatesMode } from "./types.js";
import { getSubscriptionKey, getFlattenedNestedFieldValuesDenormalized } from "./utils.js";
import "./RealtimeNotification.js";
import { viewsSelector } from "../view/selectors.js";
import "react-redux";
import "react-router";
class RealtimeEpicsBuilder extends BaseEpicsBuilder {
  _fetchServiceFactory;
  _realtimeService;
  _schemaMapper;
  _entityActions;
  _realtimeActions;
  _viewActions;
  constructor(realtimeService, fetchServiceFactory, schemaMapper) {
    super();
    this._realtimeService = realtimeService;
    this._fetchServiceFactory = fetchServiceFactory;
    this._schemaMapper = schemaMapper;
    this._entityActions = getEntityActions();
    this._realtimeActions = getRealtimeActions();
    this._viewActions = getViewActions();
  }
  buildEpicList() {
    const subscribeEpics = this.buildSubscribeEpics();
    const notifyEpics = this.buildNotifyEpics();
    const updateViewsEpics = this.buildUpdateViewsEpic();
    return [...subscribeEpics, ...notifyEpics, ...updateViewsEpics];
  }
  buildSubscribeEpics() {
    const subscribeEpic = (action$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.subscribe)),
      switchMap((action) => {
        return this._realtimeService.subscribe({
          entity: action.payload.entity,
          triggers: action.payload.triggers?.map((t) => ({
            ...t,
            key: getSubscriptionKey(action.payload.entity, t.type, t.args)
          })) ?? []
        }).pipe(
          filter((res) => res.responsePayload?.length && !res.errors?.length),
          map((res) => {
            return this._realtimeActions.event({
              items: res.responsePayload,
              entity: res.requestPayload.entity,
              trigger: res.requestPayload.triggers?.[0]
            });
          })
        );
      })
    );
    const saveSubscriberEpic = (action$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.subscribe)),
      filter((action) => !action.payload.pauseResume),
      switchMap((action) => {
        return of(this._realtimeActions.saveSubscriber({ ...action.payload }));
      })
    );
    const unsubscribeEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.unsubscribe)),
      mergeMap((action) => {
        const { entity, triggers, pauseResume } = action.payload;
        const unsubcribeTriggers = triggers?.flatMap((trigger) => {
          const subscriptionKey = getSubscriptionKey(entity, trigger.type, trigger.args);
          const activeSubscribers = state$?.value?.realtime?.subscribers?.[entity]?.[subscriptionKey]?.length;
          return activeSubscribers > 1 && !pauseResume ? [] : [trigger];
        });
        return unsubcribeTriggers?.length ? this._realtimeService.unsubscribe({
          entity,
          triggers: unsubcribeTriggers?.map((t) => ({ ...t, key: getSubscriptionKey(entity, t.type, t.args) }))
        }).pipe(mergeMap(() => of())) : of();
      })
    );
    const removeSubscriberEpic = (action$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.unsubscribe)),
      filter((action) => !action.payload.pauseResume),
      switchMap((action) => {
        return of(this._realtimeActions.removeSubscriber({ ...action.payload }));
      })
    );
    return [subscribeEpic, saveSubscriberEpic, unsubscribeEpic, removeSubscriberEpic];
  }
  buildNotifyEpics() {
    const notifyBufferTime = 2e3;
    const notifyUpdates = (action$, state$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.event)),
      bufferTime(notifyBufferTime),
      mergeMap((actions) => {
        const viewsState = viewsSelector(state$.value);
        const entityState = entitiesSelector(state$.value);
        const realtimeState = realtimeSelector(state$.value);
        const views = this.getSubscribedViews(actions, realtimeState);
        const subscribedEntities = this.getSubscribedEntities(actions, realtimeState);
        const viewsData = [...new Set(views)].map((view) => viewsState[view]);
        const latestReceivedAtByEntity = actions.reduce(
          (result, action) => {
            const { entity, receivedAtTimestamp } = action.payload;
            const timestamp = receivedAtTimestamp ?? Date.now();
            result[entity] = Math.max(result[entity] ?? 0, timestamp);
            return result;
          },
          {}
        );
        const fetchPayloads = this.prepareFetchActionPayloadsForIdleViews(viewsData, subscribedEntities);
        const now = Date.now();
        const fetchRequests = fetchPayloads.map((requestPayload) => {
          const latestReceivedAt = latestReceivedAtByEntity[requestPayload.type] ?? now;
          const delay = Math.max(0, notifyBufferTime - (now - latestReceivedAt));
          return timer(delay).pipe(mergeMap(() => this._fetchServiceFactory(requestPayload.type).get(requestPayload)));
        });
        const fetchActions = merge(...fetchRequests).pipe(
          map((res) => {
            let resultAction = null;
            if (res.status === ServerResponseStatus.Success) {
              const { type, filter: filter2 } = res.requestPayload;
              const { items, total } = res.responsePayload;
              const { fetchedKeys } = this.normalizeResponse(type, items);
              let updatedViews = this.findUpdatedViewsByKeys(type, filter2, fetchedKeys, total, viewsState);
              if (!updatedViews.length) {
                updatedViews = this.findUpdatedViewsByFieldValues(type, filter2, items, viewsState, entityState);
              }
              resultAction = this.prepareUpdatesTriggerActionPayload(updatedViews, type, filter2, items, total);
            }
            return resultAction;
          }),
          filter((res) => res !== null),
          mergeMap((action) => {
            return of(action);
          })
        );
        const retriedActionPayloads = this.prepareRetriedActionPayloadsForInProgressViews(actions, views, viewsState);
        const retryActions = retriedActionPayloads.length > 0 ? merge(retriedActionPayloads.map((e) => this._realtimeActions.event(e))) : of();
        return merge(retryActions, fetchActions);
      })
    );
    return [notifyUpdates];
  }
  buildUpdateViewsEpic() {
    const updateViewsEpic = (action$, state$) => action$.pipe(
      filter(isActionOf(this._realtimeActions.updateViews)),
      mergeMap(() => {
        const realtime = realtimeSelector(state$.value);
        const updates = {};
        Object.keys(realtime.updates).map((view) => {
          realtime.updates[view].entities.forEach((entity) => {
            if (!updates[entity]) {
              updates[entity] = [];
            }
            if (!updates[entity].includes(view)) {
              updates[entity].push(view);
            }
          });
        });
        const actions = Object.keys(updates).map(
          (entity) => this._viewActions.update({ entity, views: updates[entity] })
        );
        actions.push(this._realtimeActions.cleanUpdatedViews());
        return of(...actions);
      })
    );
    return [updateViewsEpic];
  }
  getSubscribedViews(actions, realtimeState) {
    const views = [];
    actions.forEach((action) => {
      const { entity, trigger } = action.payload;
      const subscriptionKey = getSubscriptionKey(entity, trigger.type, trigger.args);
      const viewsPerEntityAndTrigger = realtimeState.subscribers?.[entity]?.[subscriptionKey];
      if (viewsPerEntityAndTrigger?.length) {
        views.push(...viewsPerEntityAndTrigger);
      }
    });
    return views;
  }
  getSubscribedEntities(actions, realtimeState) {
    const subscribedEntities = {};
    actions.forEach((action) => {
      const { entity, trigger } = action.payload;
      const subscriptionKey = getSubscriptionKey(entity, trigger.type, trigger.args);
      const viewsPerEntityAndTrigger = realtimeState.subscribers?.[entity]?.[subscriptionKey];
      if (viewsPerEntityAndTrigger?.length) {
        subscribedEntities[entity] = true;
      }
    });
    return subscribedEntities;
  }
  prepareRetriedActionPayloadsForInProgressViews(actions, subscribedViews, viewsState) {
    const retriedEvents = [];
    actions.forEach((action) => {
      const { entity } = action.payload;
      subscribedViews.forEach((viewType) => {
        if (viewsState?.[viewType]?.entities?.[entity]?.status === "inProgress") {
          retriedEvents.push(action.payload);
        }
      });
    });
    return retriedEvents;
  }
  prepareFetchActionPayloadsForIdleViews(viewStates, subscribedEntities) {
    const fetchPayloads = [];
    viewStates.forEach((viewState) => {
      Object.values(viewState.entities).filter((e) => subscribedEntities[e.entity] && e.status === "idle").forEach((viewEntity) => {
        let fetchRequest = fetchPayloads.find((r) => r.type === viewEntity.entity && r.filter === viewEntity.filter);
        if (!fetchRequest) {
          fetchRequest = { type: viewEntity.entity, filter: viewEntity.filter, fields: [] };
          fetchPayloads.push(fetchRequest);
        }
        fetchRequest.fields = viewEntity.fields?.length ? [...new Set(fetchRequest.fields.concat(viewEntity.fields))] : fetchRequest.fields;
      });
    });
    return fetchPayloads;
  }
  normalizeResponse(type, items) {
    let fetchedEntities = {};
    let fetchedKeys = [];
    if (items?.length) {
      const schema = this._schemaMapper.getSchema(type);
      const normalizedItems = normalize(items, [schema]);
      fetchedEntities = normalizedItems.entities;
      fetchedKeys = normalizedItems.result;
    }
    return { fetchedEntities, fetchedKeys };
  }
  denormalizeKeys(type, keys, state) {
    let result = [];
    if (keys?.length) {
      const schema = this._schemaMapper.getSchema(type);
      result = denormalize(keys, [schema], state);
    }
    return result;
  }
  findUpdatedViewsByKeys(type, filter2, fetchedKeys, total, viewState) {
    let result = [];
    const entityFilterBasedViews = Object.values(viewState)?.filter((v) => v.entities[type]?.filter === filter2);
    if (entityFilterBasedViews?.length) {
      const firstView = entityFilterBasedViews[0].entities[type];
      const existingKeys = firstView.keys;
      const existingTotal = firstView.total;
      if (total !== existingTotal || fetchedKeys.length !== existingKeys.length || fetchedKeys.find((key, index) => !equals(key, existingKeys[index]))) {
        result = entityFilterBasedViews;
      }
    }
    return result;
  }
  findUpdatedViewsByFieldValues(type, filter2, items, viewState, entityState) {
    const result = [];
    const entityFilterBasedViews = Object.values(viewState)?.filter((v) => v.entities[type]?.filter === filter2);
    entityFilterBasedViews.forEach((view) => {
      const viewEntityState = view.entities[type];
      const denormalizedEntityState = this.denormalizeKeys(type, view.entities[type].keys, entityState.entities);
      const changed = viewEntityState.fields?.find((field) => {
        const fetchedFieldValues = getFlattenedNestedFieldValuesDenormalized(field, items);
        const stateFieldValues = getFlattenedNestedFieldValuesDenormalized(field, denormalizedEntityState);
        return fetchedFieldValues.length !== stateFieldValues.length || fetchedFieldValues.find((value, index) => !equals(value, stateFieldValues[index]));
      }) ?? null;
      if (changed) {
        result.push(view);
      }
    });
    return result;
  }
  prepareUpdatesTriggerActionPayload(updatedViews, type, filter2, items, total) {
    let result = null;
    if (updatedViews?.length) {
      const silentlyUpdatedViews = updatedViews.filter((v) => v.realtime[type].mode === RealtimeUpdatesMode.Silent);
      if (silentlyUpdatedViews?.length) {
        result = this._entityActions.fetchRequest.success(
          {
            requestPayload: {
              fields: [...new Set(updatedViews.flatMap((v) => v.entities[type].fields))],
              filter: filter2,
              type
            },
            responsePayload: {
              items,
              total
            },
            status: ServerResponseStatus.Success
          },
          {}
        );
      } else {
        result = this._realtimeActions.saveUpdatedViews({
          entity: type,
          views: updatedViews.map((v) => v.viewType)
        });
      }
    }
    return result;
  }
}
export {
  RealtimeEpicsBuilder
};
//# sourceMappingURL=epics.js.map
